import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  Box,
  Grid,
  InputAdornment,
  Slider,
  Typography,
  styled,
} from "@mui/material";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { MemoizedButton } from "../../SDK/button";
import { MemoizedSelectDropDown } from "../../SDK/selectDropdown/selectDropdown";
import { StateOptionsForEMI, VehiclesOptions } from "../../constants";
import { primary } from "../../constants/theme";
import { imagesWEBP } from "../../constants/webpImages";
import { Context } from "../../context/context";
import { sendAnalytics } from "../../utils/analyticsCommon";
import { pmt } from "../../utils/emiCalculation";

const CustomEMICalculator = styled(Box)(({ theme }) => ({
  marginBottom: "8px",
  width: "85%",
  margin: "16px auto 8px auto",
  ".heading": {
    fontWeight: "700",
    color: primary.black,
    fontSize: "24px",
    textAlign: "left",
    marginBottom: "8px",
  },
  ".price_box_container": {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
  },
  ".state-input-dropdown": {
    background: "none",
    color: primary.white,
    fontSize: "18px",
    border: `1px solid ${primary.white}`,
    ".MuiSelect-icon": {
      color: "white",
    },
    ".MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
  },
  ".Quotation_btn": {
    background: `${primary.lightMain} !important`,
  },
  ".dropdownContainer": {
    marginBottom: "8px",
  },
  ".red_box_container": {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    padding: "22px 12px",
    borderRadius: "4px",
    color: primary.white,
    background:
      "linear-gradient(151.45deg, #AC3750 17.62%, #F63B63 104.34%, rgba(246, 59, 99, 0) 104.35%)",
    gap: "4px",
    ".price_box": {
      display: "flex",
      justifyContent: "space-between",
      flexDirection: "column",
      margin: "4px",
      marginBottom: "16px",
    },
    ".price_title": {
      fontSize: "16px",
    },
    ".price": {
      fontSize: "24px",
      fontWeight: "700",
    },
  },
  ".slider": {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  },
  ".bodyType_container": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  ".bodyType_box": {
    border: `2px solid ${primary.lightGrey}`,
    height: "50px",
    borderRadius: "4px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "8px 0",
    padding: "4px 6px",
    ".bodyType_img": {
      objectFit: "contain",
      maxHeight: "36px",
      minWidth: "50px",
    },
    "&.active": {
      background: primary?.lightGreen,
      border: `2px solid ${primary.mainGreen}`,
    },
  },

  [theme.breakpoints.down("md")]: {
    width: "90%",
    ".heading": {
      marginLeft: "0",
    },
    ".slider": {
      maxWidth: "100%",
    },
    ".bodyType_box": {
      margin: "8px 0",
      padding: "2px",
      height: "60px",
      ".bodyType_img": {
        objectFit: "contain",
        maxHeight: "36px",
        minWidth: "45px",
        marginRight: "8px",
      },
    },
    ".state-input-dropdown": {
      fontSize: "12px",
    },
    ".bodyType_container": {
      justifyContent: "flex-start",
      gap: "12px",
    },
  },
}));

const EMIcalculator = ({ vehiclesData, showHeader, loanCta }) => {
  const [bodyType, setBodyType] = useState("pickup");
  const [selectedVehicle, setSelectedVehicle] = useState(VehiclesOptions[1]);
  const [currentVehicle, setCurrentVehicle] = useState(vehiclesData[1]);
  const [downpaymentAmount, setDownpaymentAmount] = useState(50000);
  const [price, setPrice] = useState();
  const [loanAmount, setLoanAmount] = useState();
  const [calculatedEMI, setCalculatedEMI] = useState();
  const state = useContext(Context);
  const navigate = useRouter();
  const { showLoanPopup } = state;
  const [location, setLocation] = useState(StateOptionsForEMI[0]?.value);
  const handleChange = (e) => {
    const id = e?.target?.value;
    setSelectedVehicle(VehiclesOptions?.find((item) => item?.value === id));
  };
  const handleStateChange = (e) => {
    setLocation(e?.target?.value);
    setDownpaymentAmount(50000);
  };

  useEffect(() => {
    // This will run whenever selectedVehicle  changes.
    setCurrentVehicle(
      vehiclesData?.find((item) => item?.vehicleId === selectedVehicle?.value)
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  useEffect(() => {
    // This will run whenever currentVehicle changes.
    if (currentVehicle) {
      const othersKeys = Object.keys(currentVehicle?.prices[location]);
      const firstKey = othersKeys[0];
      setPrice(
        parseInt(currentVehicle?.prices[location][firstKey]?.price * 100000)
      );
      setBodyType(firstKey);
    }
  }, [currentVehicle, location]);

  useEffect(() => {
    // This will run whenever price or downpaymentAmount changes.
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, downpaymentAmount, bodyType]);

  //    function pmt( rate, nper, pv, fv = 0, end = true ) {
  //   const when = end ? 0 : 1;
  //   const temp = Math.pow(1 + rate, nper);
  //   const maskedRate = rate === 0 ? 1 : rate;
  //   const fact = rate === 0 ? nper : ((1 + maskedRate * when) * (temp - 1) / maskedRate);
  //   return (fv + pv * temp) / fact;
  // }

  const handleSubmit = () => {
    if (currentVehicle) {
      let amount = price - downpaymentAmount;
      let rate = 0.1875 / 12;
      let term = 36;

      let emi = Math.ceil(pmt(rate, term, amount));

      // let emi = parseInt(
      //   amount *
      //     rate *
      //     (Math.pow(1 + rate, term) / (Math.pow(1 + rate, term) - 1))
      // );
      setCalculatedEMI(emi);
      setLoanAmount(amount);
    }
  };

  return (
    <CustomEMICalculator>
      <Box>
        {showHeader && (
          <Typography variant="h1" className="heading">
            <Box sx={{ display: { xs: "block", md: "none", lg: "none" } }}>
              <ArrowBackIosIcon
                onClick={() => navigate.back()}
                color="black"
                className="back_arrow"
              />
              EMI Calculator
            </Box>
            <Box sx={{ display: { xs: "none", md: "block", lg: "block" } }}>
              EMI Calculator
            </Box>
          </Typography>
        )}
        <Grid
          container
          sx={{ justifyContent: "space-between" }}
          className="emi-calculator-section"
        >
          <Grid
            container
            xs={12}
            md={7}
            sx={{
              maxWidth: { xs: "none", md: "90%" },
            }}
          >
            <Grid item xs={12} className="dropdownContainer">
              <MemoizedSelectDropDown
                className="input-dropdown"
                //   form={form}
                id={"model"}
                name="model"
                required={true}
                value={selectedVehicle?.value}
                title={"Select Popular Model"}
                optionsData={VehiclesOptions}
                onChange={(e) => {
                  handleChange(e);
                  setDownpaymentAmount(50000);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <LazyLoadImage
                      src={selectedVehicle?.vehicle_logo}
                      visibleByDefault={true}
                      style={{
                        objectFit: "contain",
                        maxWidth: "50px",
                        maxHeight: "50px",
                      }}
                      alt="vehicle logo"
                    />
                  </InputAdornment>
                }
                //   error={!!checkError("state", form)}
                // helperText={form.errors.state}
              />
            </Grid>
            <Grid item xs={12} className="red_box_container">
              <Grid container className="price_box_container">
                <Box className="price_box">
                  <Typography variant="p" style={{ fontSize: "20px" }}>
                    EMI per month
                  </Typography>
                  <Typography
                    variant="p"
                    style={{ fontSize: "38px", fontWeight: "700" }}
                  >
                    ₹ {calculatedEMI?.toLocaleString("en-IN")}*
                  </Typography>
                </Box>
                <Box className="price_box" style={{ width: "40%" }}>
                  <MemoizedSelectDropDown
                    className="state-input-dropdown"
                    title={"Select State"}
                    value={location}
                    error={true}
                    optionsData={StateOptionsForEMI}
                    onChange={(e) => handleStateChange(e)}
                  />
                </Box>
              </Grid>
              <Box style={{ display: "flex", justifyContent: "space-between" }}>
                <Box>
                  <Box className="price_box">
                    <Typography variant="p" className="price_title">
                      Loan Amount
                    </Typography>
                    <Typography variant="p" className="price">
                      ₹ {loanAmount?.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Box className="price_box">
                    <Typography variant="p" className="price_title">
                      Downpayment
                    </Typography>
                    <Typography variant="p" className="price">
                      ₹ {downpaymentAmount?.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Box className="price_box">
                    <Typography variant="p" className="price_title">
                      Interest Rate
                    </Typography>
                    <Typography variant="p" className="price">
                      10.5% *
                    </Typography>
                  </Box>
                  <Box className="price_box">
                    <Typography variant="p" className="price_title">
                      Tenure
                    </Typography>
                    <Typography variant="p" className="price">
                      36 Month
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} className="slider">
              <Slider
                sx={{
                  color: primary.black,
                  height: "8px",
                  margin: "0px",
                  margin: "auto",
                  display: "block",
                  width: "100%",
                }}
                max={price} //loanAmount
                min={50000}
                defaultValue={downpaymentAmount}
                value={downpaymentAmount}
                step={1000}
                onChange={(e) => {
                  if (e?.target?.value >= 50000) {
                    setDownpaymentAmount(e?.target?.value);
                    handleSubmit(e);
                  } else {
                    setDownpaymentAmount(50000);
                  }
                }}
              />
              <Box style={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="p"
                  style={{ fontSize: "12px", color: primary.black }}
                >
                  Downpayment ₹50,000
                </Typography>
                <Typography
                  variant="p"
                  style={{ fontSize: "12px", color: primary.black }}
                >
                  Vehicle Price: ₹{price?.toLocaleString("en-IN")}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            xs={12}
            md={5}
            direction={"column"}
            sx={{ maxWidth: { md: "40%", xs: "none" } }}
            style={{ alignSelf: "flex-end" }}
          >
            <Box className="bodyType_container">
              {currentVehicle?.prices?.Others?.flat_bed && (
                <Box
                  className={
                    bodyType === "flat_bed"
                      ? "bodyType_box active"
                      : "bodyType_box"
                  }
                  style={{
                    opacity: currentVehicle?.prices?.Others?.flat_bed
                      ? "1"
                      : "0",
                  }}
                  onClick={() => {
                    setBodyType("flat_bed");
                    setDownpaymentAmount(50000);
                    setPrice(
                      parseInt(
                        currentVehicle?.prices?.Others?.flat_bed?.price * 100000
                      )
                    );
                  }}
                >
                  <LazyLoadImage
                    src={
                      bodyType === "flat_bed"
                        ? imagesWEBP?.dummyImages?.flat_bedselected
                        : imagesWEBP?.dummyImages?.flat_bedUnselected
                    }
                    visibleByDefault={true}
                    alt={"Flat Bed Image"}
                    className="bodyType_img"
                  />
                  <Typography style={{ fontSize: "12px" }}>Flat Bed</Typography>
                </Box>
              )}
              {currentVehicle?.prices?.Others?.pickup && (
                <Box
                  className={
                    bodyType === "pickup"
                      ? "bodyType_box active"
                      : "bodyType_box"
                  }
                  style={{
                    opacity: currentVehicle?.prices?.Others?.pickup ? "1" : "0",
                  }}
                  onClick={() => {
                    setBodyType("pickup");
                    setDownpaymentAmount(50000);
                    setPrice(
                      parseInt(
                        currentVehicle?.prices?.Others?.pickup?.price * 100000
                      )
                    );
                  }}
                >
                  <LazyLoadImage
                    src={
                      bodyType === "pickup"
                        ? imagesWEBP?.dummyImages?.pickupselected
                        : imagesWEBP?.dummyImages?.pickupUnselected
                    }
                    visibleByDefault={true}
                    alt={"pickup image"}
                    className="bodyType_img"
                  />
                  <Typography style={{ fontSize: "12px" }}>Pickup</Typography>
                </Box>
              )}
              {currentVehicle?.prices?.Others?.full_body && (
                <Box
                  className={
                    bodyType === "full_body"
                      ? "bodyType_box active"
                      : "bodyType_box"
                  }
                  style={{
                    opacity: currentVehicle?.prices?.Others?.full_body
                      ? "1"
                      : "0",
                  }}
                  onClick={() => {
                    setBodyType("full_body");
                    setDownpaymentAmount(50000);
                    setPrice(
                      parseInt(
                        currentVehicle?.prices?.Others?.full_body?.price *
                          100000
                      )
                    );
                  }}
                >
                  <LazyLoadImage
                    src={
                      bodyType === "full_body"
                        ? imagesWEBP?.dummyImages?.full_bodyselected
                        : imagesWEBP?.dummyImages?.full_bodyUnselected
                    }
                    visibleByDefault={true}
                    alt={"Full Body Image"}
                    className="bodyType_img"
                  />
                  <Typography style={{ fontSize: "12px" }}>
                    Full Body
                  </Typography>
                </Box>
              )}
            </Box>
            <Box className="CTA_buttons_container">
              <MemoizedButton
                sx={{
                  background: `${primary.main} !important`,
                  boxShadow: "none",
                  border: "none",
                  borderRadius: "7px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  width: "100%",
                  marginBlock: "8px",
                }}
                handleClick={() => {
                  let payload = {
                    vehicle_id: currentVehicle?.id,
                    vehicle_name: currentVehicle?.name,
                    downpayment: downpaymentAmount,
                    bodyType: bodyType,
                  };
                  sendAnalytics(
                    "v2_emi_calculator_applyLoan_clicked",
                    payload,
                    "en"
                  );

                  const loanPayload = {
                    formTrigger: "v2_apply_for_loan_emi_calculator",
                    brand: currentVehicle?.brand,
                    vehicle_id: currentVehicle?.vehicle_path_id,
                    headingText: "Enter Your Mobile Number",
                    CTAText: "Apply for Loan",
                  };
                  if (localStorage?.getItem("phoneInfo") === null) {
                    showLoanPopup(loanPayload);
                  } else {
                    navigate.push("/electric-commercial-vehicles");
                  }
                }}
                content={loanCta ?? "Apply for Loan"}
              />
              {/* <MemoizedButton
                sx={{
                  background: `#FEEBEF!important`,
                  boxShadow: "none",
                  border: "none",
                  borderRadius: "7px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  width: "100%",
                  marginTop: "8px",
                  color: `${primary?.main}`,
                }}
                className="Quotation_btn"
                handleClick={() => {
                  let payload = {
                    vehicle_id: currentVehicle?.id,
                    vehicle_name: currentVehicle?.name,
                    downpayment: downpaymentAmount,
                    bodyType: bodyType,
                  };
                  sendAnalytics(
                    "v2_emi_calculator_get_quotation_clicked",
                    payload,
                    "en"
                  );

                  const loanPayload = {
                    formTrigger: "v2_get_quotation_emi_calculator",
                    brand: currentVehicle?.brand,
                    vehicle_id: currentVehicle?.vehicle_path_id,
                    headingText: "Enter Your Mobile Number",
                    CTAText: "Get Quotation",
                  };
                  if (localStorage?.getItem("phoneInfo") === null) {
                    showLoanPopup(loanPayload);
                  } else {
                    navigate.push("/electric-commercial-vehicles");
                  }
                }}
                content={"Get Vehicle Quotation"}
              /> */}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </CustomEMICalculator>
  );
};

export default EMIcalculator;
